import api from "../api/axios";
import { getNativePlatform, isNativeApp } from "./runtime";

export const MOBILE_UPDATE_STATUS = Object.freeze({
  NONE: "none",
  OPTIONAL: "optional",
  REQUIRED: "required",
});

export const MOBILE_UPDATE_CHECK_TIMEOUT_MS = 5000;
export const OPTIONAL_UPDATE_DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

function toBuildNumber(value) {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized >= 0 ? normalized : null;
}

export function normalizeMobileUpdatePolicy(rawPolicy, expectedPlatform) {
  const platform = String(rawPolicy?.platform || "").trim().toLowerCase();
  const latestBuild = toBuildNumber(rawPolicy?.latest_build);
  const minimumSupportedBuild = toBuildNumber(rawPolicy?.minimum_supported_build);
  const storeUrl = String(rawPolicy?.store_url || "").trim();
  const validPlatform = platform === expectedPlatform;
  const validBuildRange =
    latestBuild !== null &&
    minimumSupportedBuild !== null &&
    minimumSupportedBuild <= latestBuild;
  const isEnabled =
    rawPolicy?.is_enabled === true &&
    validPlatform &&
    validBuildRange &&
    latestBuild > 0 &&
    Boolean(storeUrl);

  return {
    platform: validPlatform ? platform : String(expectedPlatform || ""),
    isEnabled,
    latestBuild: validBuildRange ? latestBuild : 0,
    minimumSupportedBuild: validBuildRange ? minimumSupportedBuild : 0,
    storeUrl: isEnabled ? storeUrl : "",
  };
}

export function getMobileUpdateStatus(currentBuildValue, policy) {
  const currentBuild = toBuildNumber(currentBuildValue);
  if (currentBuild === null || !policy?.isEnabled) {
    return MOBILE_UPDATE_STATUS.NONE;
  }
  if (currentBuild < policy.minimumSupportedBuild) {
    return MOBILE_UPDATE_STATUS.REQUIRED;
  }
  if (currentBuild < policy.latestBuild) {
    return MOBILE_UPDATE_STATUS.OPTIONAL;
  }
  return MOBILE_UPDATE_STATUS.NONE;
}

export async function readMobileUpdatePolicy() {
  if (!isNativeApp()) {
    return {
      status: MOBILE_UPDATE_STATUS.NONE,
      currentBuild: null,
      policy: null,
    };
  }

  const platform = getNativePlatform();
  if (!new Set(["ios", "android"]).has(platform)) {
    return {
      status: MOBILE_UPDATE_STATUS.NONE,
      currentBuild: null,
      policy: null,
    };
  }

  const { App } = await import("@capacitor/app");
  const appInfo = await App.getInfo();
  const currentBuild = toBuildNumber(appInfo?.build);
  const response = await api.get("/mobile/version-policy/", {
    params: { platform },
    timeout: MOBILE_UPDATE_CHECK_TIMEOUT_MS,
    meta: {
      authOptional: true,
      skipNativeAuth: true,
      suppressGlobalError: true,
      suppressUnauthorizedRedirect: true,
    },
  });
  const policy = normalizeMobileUpdatePolicy(response?.data, platform);

  return {
    status: getMobileUpdateStatus(currentBuild, policy),
    currentBuild,
    policy,
  };
}

export function getMobileStoreUrlCandidates(platform, storeUrl) {
  const rawUrl = String(storeUrl || "").trim();
  if (!rawUrl) return [];

  const candidates = [];
  try {
    const parsed = new URL(rawUrl);
    if (platform === "ios" && parsed.hostname.endsWith("apps.apple.com")) {
      candidates.push(`itms-apps://${parsed.host}${parsed.pathname}${parsed.search}`);
    }
    if (platform === "android" && parsed.hostname.endsWith("play.google.com")) {
      const packageName = parsed.searchParams.get("id");
      if (packageName) {
        candidates.push(`market://details?id=${encodeURIComponent(packageName)}`);
      }
    }
  } catch {
    // A server-provided native store scheme is still a valid direct candidate.
  }
  candidates.push(rawUrl);
  return [...new Set(candidates)];
}

export async function openMobileStoreListing(platform, storeUrl) {
  const candidates = getMobileStoreUrlCandidates(platform, storeUrl);
  if (!candidates.length) {
    throw new Error("Mobile store URL is not configured");
  }

  const { AppLauncher } = await import("@capacitor/app-launcher");
  let lastError = null;
  for (const url of candidates) {
    try {
      const result = await AppLauncher.openUrl({ url });
      if (result?.completed !== false) return true;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("Could not open the mobile store");
}

function getOptionalUpdateDismissalKey(platform, latestBuild) {
  return `vg_mobile_update_dismissed_${platform}_${latestBuild}`;
}

export function dismissOptionalMobileUpdate(platform, latestBuild, now = Date.now()) {
  try {
    localStorage.setItem(
      getOptionalUpdateDismissalKey(platform, latestBuild),
      String(now)
    );
  } catch {
    // Session-only dismissal is handled by the controller state.
  }
}

export function wasOptionalMobileUpdateDismissed(
  platform,
  latestBuild,
  now = Date.now()
) {
  try {
    const dismissedAt = Number(
      localStorage.getItem(getOptionalUpdateDismissalKey(platform, latestBuild))
    );
    return (
      Number.isFinite(dismissedAt) &&
      dismissedAt > 0 &&
      now - dismissedAt < OPTIONAL_UPDATE_DISMISSAL_TTL_MS
    );
  } catch {
    return false;
  }
}
